import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { emailTemplateAPI } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Copy, FileText } from "lucide-react";

const VAR_HELP = [
  { var: "{client_name}", desc: "Nom du contact" },
  { var: "{company}", desc: "Société du client" },
  { var: "{company_name}", desc: "Nom de votre société" },
];

const DEFAULT_TEMPLATES = [
  { name: "Bienvenue", subject: "Bienvenue chez {company_name}", body: "Bonjour {client_name},\n\nNous sommes ravis de vous compter parmi nos clients.\n\nN'hesitez pas a nous contacter pour toute question.\n\nCordialement,\n{company_name}" },
  { name: "Newsletter mensuelle", subject: "Les nouveautes du mois - {company_name}", body: "Bonjour {client_name},\n\nDecouvrez nos dernières actualites et nouveautes.\n\n[Vos actualites ici]\n\nA bientot,\n{company_name}" },
  { name: "Offre speciale", subject: "Offre exclusive pour vous, {client_name}", body: "Bonjour {client_name},\n\nEn tant que client privilegie, nous avons le plaisir de vous proposer une offre speciale.\n\n[Details de l'offre]\n\nCette offre est valable jusqu'au [date].\n\nCordialement,\n{company_name}" },
  { name: "Relance", subject: "Avez-vous des questions ? - {company_name}", body: "Bonjour {client_name},\n\nNous revenons vers vous suite a notre dernier echange.\n\nAvez-vous des questions ou souhaitez-vous en discuter davantage ?\n\nNous restons a votre disposition.\n\nCordialement,\n{company_name}" },
];

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", subject: "", body: "" });
  const [showPreview, setShowPreview] = useState(null);

  const load = useCallback(() => { emailTemplateAPI.list().then(r => setTemplates(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm({ name: "", subject: "", body: "" }); setShowDialog(true); };
  const openEdit = (t) => { setEditId(t.id); setForm({ name: t.name, subject: t.subject, body: t.body }); setShowDialog(true); };

  const handleSave = async () => {
    if (!form.name || !form.subject || !form.body) { toast.error("Tous les champs sont requis"); return; }
    try {
      if (editId) { await emailTemplateAPI.update(editId, form); toast.success("Template mis à jour"); }
      else { await emailTemplateAPI.create(form); toast.success("Template créé"); }
      setShowDialog(false);
      load();
    } catch { toast.error("Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce template ?")) return;
    try { await emailTemplateAPI.delete(id); toast.success("Supprimé"); load(); } catch { toast.error("Erreur"); }
  };

  const handleDuplicate = async (t) => {
    try {
      await emailTemplateAPI.create({ name: `${t.name} (copie)`, subject: t.subject, body: t.body });
      toast.success("Template dupliqué");
      load();
    } catch { toast.error("Erreur"); }
  };

  const initDefaults = async () => {
    try {
      for (const tpl of DEFAULT_TEMPLATES) {
        await emailTemplateAPI.create(tpl);
      }
      toast.success(`${DEFAULT_TEMPLATES.length} templates crees`);
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="templates-title">Templates d'emails</h1>
          <p className="text-sm text-slate-500 mt-1">{templates.length} template(s)</p>
        </div>
        <div className="flex gap-2">
          {templates.length === 0 && (
            <Button variant="outline" size="sm" onClick={initDefaults} data-testid="init-defaults-btn"><FileText size={14} className="mr-1.5" />Charger les modeles par defaut</Button>
          )}
          <Button size="sm" onClick={openCreate} data-testid="new-template-btn"><Plus size={14} className="mr-1.5" />Nouveau template</Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Nom</TableHead>
              <TableHead>Objet</TableHead>
              <TableHead className="text-center">Modifie</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  <p className="text-slate-400">Aucun template</p>
                  <p className="text-xs text-slate-300 mt-1">Cliquez sur "Charger les modeles par defaut" pour commencer</p>
                </TableCell>
              </TableRow>
            )}
            {templates.map(t => (
              <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                <TableCell className="font-medium text-sm">{t.name}</TableCell>
                <TableCell className="text-sm text-slate-500 max-w-[300px] truncate">{t.subject}</TableCell>
                <TableCell className="text-center text-xs text-slate-500">{t.updated_at ? new Date(t.updated_at).toLocaleDateString("fr-FR") : "-"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowPreview(t)} title="Apercu" data-testid={`preview-template-${t.id}`}><FileText size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(t)} title="Modifier" data-testid={`edit-template-${t.id}`}><Pencil size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500" onClick={() => handleDuplicate(t)} title="Dupliquer" data-testid={`dup-template-${t.id}`}><Copy size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(t.id)} title="Supprimer" data-testid={`delete-template-${t.id}`}><Trash2 size={13} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={showDialog} onOpenChange={v => { if (!v) setShowDialog(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Modifier le template" : "Nouveau template"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nom du template *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Newsletter mensuelle" data-testid="template-name" /></div>
            <div><Label>Objet du mail *</Label><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Ex: Nos nouveautes du mois" data-testid="template-subject" /></div>
            <div>
              <Label>Contenu *</Label>
              <Textarea rows={10} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} placeholder={"Bonjour {client_name},\n\n..."} data-testid="template-body" />
              <div className="flex flex-wrap gap-2 mt-2">
                {VAR_HELP.map(v => (
                  <button key={v.var} type="button" className="text-[11px] px-2 py-0.5 bg-slate-100 text-slate-600 rounded hover:bg-indigo-50 hover:text-indigo-600 transition" onClick={() => setForm({ ...form, body: form.body + v.var })}>{v.var} <span className="text-slate-400">({v.desc})</span></button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
              <Button onClick={handleSave} data-testid="template-save-btn">{editId ? "Enregistrer" : "Creer"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!showPreview} onOpenChange={v => { if (!v) setShowPreview(null); }}>
        <DialogContent className="max-w-xl">
          {showPreview && (<>
            <DialogHeader><DialogTitle>{showPreview.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs text-slate-500">Objet</Label><div className="text-sm font-medium bg-slate-50 rounded-md p-2 border">{showPreview.subject}</div></div>
              <div><Label className="text-xs text-slate-500">Contenu</Label><div className="text-sm bg-slate-50 rounded-md p-3 border whitespace-pre-line">{showPreview.body}</div></div>
              <div className="flex justify-end"><Button variant="outline" onClick={() => setShowPreview(null)}>Fermer</Button></div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
